/**
 * useGigen Hook - Phase 4-2
 *
 * 【犠現】キーワード能力を管理するカスタムフック。
 * 既存のモンスターを生贄にして、コストを軽減して召喚する。
 *
 * 特徴:
 * - useState 1個（pendingGigenActivation）
 * - トリガーエンジンとの統合（召喚時トリガー発火）
 * - 生贄のコストに応じた軽減計算
 */

import { useState, useCallback } from 'react';
import type React from 'react';
import { calculateGigenReduction } from '../../engine/keywordAbilities';
import { registerCardTriggers, fireTrigger } from '../../engine/triggerEngine';
import { TRIGGER_TYPES } from '../../engine/triggerTypes';
import { createMonsterInstance } from '../../utils/helpers';
import type { MonsterInstance, HandCard, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type {
  PendingMonsterTarget,
  PendingHandSelection,
  PendingGraveyardSelection,
  PendingDeckReview,
  PendingGigenActivation,
} from '../../types/ui';

interface UseGigenProps {
  currentPlayer: PlayerNumber;
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  p1Graveyard: CardWithId[];
  p2Graveyard: CardWithId[];
  p1Life: number;
  p2Life: number;
  summoningSickness: boolean;
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelection | null>>;
  setPendingGraveyardSelection: React.Dispatch<React.SetStateAction<PendingGraveyardSelection | null>>;
  setShowGraveyardViewer: React.Dispatch<React.SetStateAction<PlayerNumber | null>>;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReview | null>>;
  setSelectedHandCard: React.Dispatch<React.SetStateAction<HandCard | null>>;
}

interface UseGigenReturn {
  pendingGigenActivation: PendingGigenActivation | null;
  setPendingGigenActivation: React.Dispatch<React.SetStateAction<PendingGigenActivation | null>>;
  startGigenActivation: (card: HandCard, slotIndex: number) => void;
  completeGigenSummon: (card: HandCard, slotIndex: number, sacrificeIndex: number) => void;
  cancelGigenActivation: () => void;
}

export const useGigen = ({
  currentPlayer,
  p1ActiveSP,
  p2ActiveSP,
  p1Field,
  p2Field,
  p1Hand,
  p2Hand,
  p1Deck,
  p2Deck,
  p1Graveyard,
  p2Graveyard,
  p1Life,
  p2Life,
  summoningSickness,
  addLog,
  setP1Field,
  setP2Field,
  setP1Hand,
  setP2Hand,
  setP1Deck,
  setP2Deck,
  setP1Graveyard,
  setP2Graveyard,
  setP1ActiveSP,
  setP2ActiveSP,
  setP1RestedSP,
  setP2RestedSP,
  setP1Life,
  setP2Life,
  setPendingMonsterTarget,
  setPendingHandSelection,
  setPendingGraveyardSelection,
  setShowGraveyardViewer,
  setPendingDeckReview,
  setSelectedHandCard,
}: UseGigenProps): UseGigenReturn => {
  // 【犠現】発動中の状態
  const [pendingGigenActivation, setPendingGigenActivation] = useState<PendingGigenActivation | null>(null);
  // pendingGigenActivation = { card, slotIndex } - 召喚するカードとスロット

  /**
   * 【犠現】発動開始（生贄選択UIを表示）
   */
  const startGigenActivation = useCallback((card: HandCard, slotIndex: number): void => {
    const myField = currentPlayer === 1 ? p1Field : p2Field;

    // フィールドにモンスターがいるか確認
    const validTargets = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('【犠現】フィールドに生贄にできるモンスターがいません', 'damage');
      return;
    }

    // 生贄選択待ち状態を設定
    const sacrificeCandidates = validTargets.map(t => t.index);
    setPendingGigenActivation({ card, slotIndex, sacrificeCandidates });
    setPendingMonsterTarget({
      message: '【犠現】生贄にするモンスターを選択',
      targetPlayer: 'self',
      callback: (sacrificeIndex) => {
        completeGigenSummon(card, slotIndex, sacrificeIndex);
      }
    });
  }, [currentPlayer, p1Field, p2Field, addLog, setPendingMonsterTarget]);

  /**
   * 【犠現】召喚完了（生贄選択後）
   */
  const completeGigenSummon = useCallback((card: HandCard, slotIndex: number, sacrificeIndex: number): void => {
    const activeSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
    const myField = currentPlayer === 1 ? p1Field : p2Field;
    const setMyField = currentPlayer === 1 ? setP1Field : setP2Field;
    const setMyHand = currentPlayer === 1 ? setP1Hand : setP2Hand;
    const setMyGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;
    const setActiveSP = currentPlayer === 1 ? setP1ActiveSP : setP2ActiveSP;
    const setRestedSP = currentPlayer === 1 ? setP1RestedSP : setP2RestedSP;

    const sacrifice = myField[sacrificeIndex];
    if (!sacrifice) {
      addLog('【犠現】生贄対象が見つかりません', 'damage');
      setPendingGigenActivation(null);
      return;
    }

    // コスト軽減を計算
    const reduction = calculateGigenReduction(sacrifice);
    const reducedCost = Math.max(0, card.cost - reduction);

    if (activeSP < reducedCost) {
      addLog(`【犠現】SPが足りません（必要: ${reducedCost}, 現在: ${activeSP}）`, 'damage');
      setPendingGigenActivation(null);
      return;
    }

    // スロットが使用中でないか確認
    if (myField[slotIndex] !== null && slotIndex !== sacrificeIndex) {
      addLog('そのスロットは使用中です', 'damage');
      setPendingGigenActivation(null);
      return;
    }

    // 生贄モンスターを墓地へ
    setMyGraveyard(prev => [...prev, sacrifice]);
    addLog(`【犠現】${sacrifice.name}を生贄に捧げた（コスト-${reduction}）`, 'info');

    // モンスターを召喚
    const monsterInstance = createMonsterInstance(card);
    monsterInstance.canAttack = !summoningSickness; // 召喚酔いOFFなら即攻撃可能
    monsterInstance.owner = currentPlayer;

    // 召喚先スロット（生贄スロットでも可）
    const targetSlot = slotIndex !== sacrificeIndex ? slotIndex : sacrificeIndex;

    setMyField(prev => {
      const newField = [...prev];
      // 生贄スロットを空にする（別スロットに召喚する場合）
      if (slotIndex !== sacrificeIndex) {
        newField[sacrificeIndex] = null;
      }
      newField[targetSlot] = monsterInstance;
      return newField;
    });

    // 手札から削除
    setMyHand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));

    // SPを消費
    if (reducedCost > 0) {
      setActiveSP(prev => Math.max(0, prev - reducedCost));
      setRestedSP(prev => prev + reducedCost);
    }

    addLog(`${card.name}を召喚！（【犠現】コスト: ${reducedCost}）`, 'info');

    // トリガー登録
    registerCardTriggers(monsterInstance, currentPlayer, targetSlot);

    // 召喚時トリガーを発火
    fireTrigger(TRIGGER_TYPES.ON_SUMMON, {
      currentPlayer,
      card: monsterInstance,
      slotIndex: targetSlot,
      setP1Life, setP2Life,
      setP1Field, setP2Field,
      setP1Hand, setP2Hand,
      setP1Deck, setP2Deck,
      setP1Graveyard, setP2Graveyard,
      p1Field: currentPlayer === 1 ? [...myField.slice(0, sacrificeIndex), null, ...myField.slice(sacrificeIndex + 1)] : p1Field,
      p2Field: currentPlayer === 2 ? [...myField.slice(0, sacrificeIndex), null, ...myField.slice(sacrificeIndex + 1)] : p2Field,
      p1Hand, p2Hand,
      p1Deck, p2Deck,
      p1Graveyard, p2Graveyard,
      p1Life, p2Life,
      addLog,
      setPendingHandSelection,
      setPendingGraveyardSelection,
      setShowGraveyardViewer,
      setPendingDeckReview,
      setPendingMonsterTarget,
    });

    setPendingGigenActivation(null);
    setSelectedHandCard(null);
  }, [currentPlayer, p1ActiveSP, p2ActiveSP, p1Field, p2Field, p1Hand, p2Hand,
      p1Deck, p2Deck, p1Graveyard, p2Graveyard, p1Life, p2Life, summoningSickness, addLog,
      setP1Field, setP2Field, setP1Hand, setP2Hand, setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
      setP1Life, setP2Life, setP1Deck, setP2Deck,
      setPendingHandSelection, setPendingGraveyardSelection, setShowGraveyardViewer, setPendingDeckReview,
      setPendingMonsterTarget, setSelectedHandCard]);

  /**
   * 【犠現】キャンセル
   */
  const cancelGigenActivation = useCallback((): void => {
    setPendingGigenActivation(null);
  }, []);

  return {
    // State
    pendingGigenActivation,
    setPendingGigenActivation,

    // Actions
    startGigenActivation,
    completeGigenSummon,
    cancelGigenActivation,
  };
};

export default useGigen;
